import { useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import {
    AppBar,
    Toolbar,
    Box,
    Container,
    IconButton,
    Typography,
    Avatar,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Tooltip,
    Drawer,
    List,
    ListItem,
    ListItemButton,
} from '@mui/material'
import {
    DarkMode,
    LightMode,
    Person,
    Logout,
    Settings,
    Dashboard,
    Inbox,
    Menu as MenuIcon,
} from '@mui/icons-material'
import { useAuthStore } from '@store/authStore'
import { useUIStore } from '@store/uiStore'
import { useTranslation } from '@i18n'
import LanguageSwitcher from '@components/Common/LanguageSwitcher'

const DRAWER_WIDTH = 240

const EndUserLayout = () => {
    const navigate = useNavigate()
    const location = useLocation()
    const { t, language } = useTranslation()
    const { user, logout } = useAuthStore()
    const { darkMode, toggleDarkMode, themeSettings } = useUIStore()
    const isRTL = language === 'ar'

    const [anchorEl, setAnchorEl] = useState(null)
    const [mobileOpen, setMobileOpen] = useState(false)
    const open = Boolean(anchorEl)

    const menuItems = [
        {
            path: '/portal/dashboard',
            icon: <Dashboard />,
            label: isRTL ? 'لوحة التحكم' : 'Dashboard',
        },
        {
            path: '/portal/settings',
            icon: <Settings />,
            label: isRTL ? 'الإعدادات' : 'Settings',
        },
    ]

    const handleClick = (event) => setAnchorEl(event.currentTarget)
    const handleClose = () => setAnchorEl(null)

    const handleLogout = () => {
        handleClose()
        logout()
        navigate('/portal')
    }

    const getUserName = () => user?.full_name || user?.email || 'User'

    const getInitials = () => {
        const name = getUserName()
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2)
    }

    const drawerContent = (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <Toolbar />
            <List sx={{ flexGrow: 1, px: 1 }}>
                {menuItems.map((item) => {
                    const isActive = location.pathname === item.path
                    return (
                        <ListItem key={item.path} disablePadding sx={{ mb: 0.5 }}>
                            <ListItemButton
                                onClick={() => {
                                    navigate(item.path)
                                    setMobileOpen(false)
                                }}
                                sx={{
                                    borderRadius: 2,
                                    bgcolor: isActive ? 'primary.main' : 'transparent',
                                    color: isActive ? 'primary.contrastText' : 'text.primary',
                                    '&:hover': {
                                        bgcolor: isActive ? 'primary.dark' : 'action.hover',
                                    },
                                    '& .MuiListItemIcon-root': {
                                        color: isActive ? 'primary.contrastText' : 'text.secondary',
                                    },
                                }}
                            >
                                <ListItemIcon sx={{ minWidth: 40 }}>
                                    {item.icon}
                                </ListItemIcon>
                                <ListItemText
                                    primary={item.label}
                                    primaryTypographyProps={{
                                        fontWeight: isActive ? 600 : 400,
                                        textAlign: isRTL ? 'right' : 'left',
                                    }}
                                />
                            </ListItemButton>
                        </ListItem>
                    )
                })}
            </List>
        </Box>
    )

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }} dir={isRTL ? 'rtl' : 'ltr'}>
            {/* Header */}
            <AppBar
                position="fixed"
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    boxShadow: 1,
                }}
            >
                <Toolbar>
                    {/* Menu Toggle */}
                    <IconButton
                        color="inherit"
                        onClick={() => setMobileOpen(!mobileOpen)}
                        sx={{ mr: 2, display: { md: 'none' } }}
                    >
                        <MenuIcon />
                    </IconButton>

                    {/* Logo */}
                    <Box
                        component="img"
                        src={themeSettings?.logoUrl || '/moj-logo.png'}
                        alt="Logo"
                        sx={{ height: 36, width: 'auto', mr: 2 }}
                    />

                    <Typography
                        variant="h6"
                        noWrap
                        sx={{
                            background: 'linear-gradient(135deg, #1a3a6b 0%, #c9a227 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                            fontWeight: 700,
                            flexGrow: 1,
                        }}
                    >
                        {isRTL ? (themeSettings?.systemNameAr || 'وزارة العدل') : (themeSettings?.systemNameEn || 'MINISTRY OF JUSTICE')}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {/* Theme Toggle */}
                        <Tooltip title={darkMode ? 'Light Mode' : 'Dark Mode'}>
                            <IconButton onClick={toggleDarkMode} color="inherit">
                                {darkMode ? <LightMode /> : <DarkMode />}
                            </IconButton>
                        </Tooltip>

                        {/* Language Switcher */}
                        <LanguageSwitcher />

                        {/* User Menu */}
                        <Tooltip title={getUserName()}>
                            <IconButton onClick={handleClick} size="small" sx={{ ml: 1 }}>
                                <Avatar sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.875rem' }}>
                                    {getInitials()}
                                </Avatar>
                            </IconButton>
                        </Tooltip>
                    </Box>

                    <Menu
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                        PaperProps={{ sx: { minWidth: 200, mt: 1 } }}
                    >
                        <Box sx={{ px: 2, py: 1.5 }}>
                            <Typography variant="subtitle1" fontWeight={600}>{getUserName()}</Typography>
                            <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
                            <Typography variant="caption" color="primary.main">
                                {isRTL ? 'مستخدم' : 'End User'}
                            </Typography>
                        </Box>
                        <Divider />
                        <MenuItem onClick={() => { handleClose(); navigate('/portal/settings'); }}>
                            <ListItemIcon><Settings fontSize="small" /></ListItemIcon>
                            <ListItemText>{isRTL ? 'الإعدادات' : 'Settings'}</ListItemText>
                        </MenuItem>
                        <Divider />
                        <MenuItem onClick={handleLogout}>
                            <ListItemIcon><Logout fontSize="small" color="error" /></ListItemIcon>
                            <ListItemText sx={{ color: 'error.main' }}>{isRTL ? 'تسجيل الخروج' : 'Logout'}</ListItemText>
                        </MenuItem>
                    </Menu>
                </Toolbar>
            </AppBar>

            {/* Sidebar - Desktop */}
            <Drawer
                variant="permanent"
                anchor={isRTL ? 'right' : 'left'}
                sx={{
                    width: DRAWER_WIDTH,
                    flexShrink: 0,
                    display: { xs: 'none', md: 'block' },
                    '& .MuiDrawer-paper': {
                        width: DRAWER_WIDTH,
                        boxSizing: 'border-box',
                        borderRight: isRTL ? 'none' : '1px solid',
                        borderLeft: isRTL ? '1px solid' : 'none',
                        borderColor: 'divider',
                    },
                }}
            >
                {drawerContent}
            </Drawer>

            {/* Sidebar - Mobile */}
            <Drawer
                variant="temporary"
                anchor={isRTL ? 'right' : 'left'}
                open={mobileOpen}
                onClose={() => setMobileOpen(false)}
                ModalProps={{ keepMounted: true }}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': { width: DRAWER_WIDTH },
                }}
            >
                {drawerContent}
            </Drawer>

            {/* Main Content */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 3,
                    pt: { xs: 10, sm: 11 },
                    bgcolor: 'background.default',
                    minHeight: '100vh',
                    ml: { md: isRTL ? 0 : 0 },
                    mr: { md: isRTL ? 0 : 0 },
                }}
            >
                <Container maxWidth="lg">
                    <Outlet />
                </Container>
            </Box>
        </Box>
    )
}

export default EndUserLayout
